import { useCallback, useEffect, useMemo } from 'react';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useEventLastReviewedContext } from 'src/contexts/SellerEventLastReviewContext/SellerEventLastReviewContext';
import { ACTIVE_EVENT_IDS_2_QUERY_PARAM } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.constants';

import { useCatchUpParams } from '../InventoryEventPage.hooks';

export const useCatchUpModeEventCards = () => {
  const { eventLastReviewed, onSettingVirtualIds } =
    useEventLastReviewedContext();

  const { viagVirtualIdsList, activeViagVirtualId, setSearchParams } =
    useCatchUpParams();

  useEffect(() => {
    onSettingVirtualIds(viagVirtualIdsList ?? []);
  }, [onSettingVirtualIds, viagVirtualIdsList]);

  const { eventsTransformed } = useCatalogDataContext();

  // Apply filters to the catchup event ids.
  const sanitizedEvents = useMemo(() => {
    return (eventsTransformed ?? []).filter((e) =>
      viagVirtualIdsList?.includes(e.event.virtId.toLocaleLowerCase())
    );
  }, [eventsTransformed, viagVirtualIdsList]);

  // Sanitize the selected index, the original active virtual id may have been filtered out
  const sanitizedSelectedIndex = useMemo(() => {
    const activeEventIndex = sanitizedEvents.findIndex(
      (e) => e.event.virtId === activeViagVirtualId
    );
    if (activeEventIndex < 0) {
      const sanitizedActiveVirtualId = sanitizedEvents[0]?.event?.virtId;
      // Update active virtual id to the first one when not found
      if (sanitizedActiveVirtualId) {
        setSearchParams((prev) => {
          prev.set(ACTIVE_EVENT_IDS_2_QUERY_PARAM, sanitizedActiveVirtualId);
          return prev;
        });
      }
      return 0;
    }
    return activeEventIndex;
  }, [activeViagVirtualId, sanitizedEvents, setSearchParams]);

  const eventCards = useMemo(() => {
    return sanitizedEvents.map((e, i) => {
      const virtId = e?.event?.virtId;
      return {
        event: e?.event ?? {},
        isActive: sanitizedSelectedIndex === i,
        isReviewed: eventLastReviewed?.[virtId] != null,
        onClick: () => {
          setSearchParams((prev) => {
            prev.set(ACTIVE_EVENT_IDS_2_QUERY_PARAM, e?.event.virtId ?? '');
            return prev;
          });
        },
      };
    });
  }, [
    eventLastReviewed,
    sanitizedEvents,
    sanitizedSelectedIndex,
    setSearchParams,
  ]);

  const onNextClick = useCallback(() => {
    const nextIndex = sanitizedSelectedIndex + 1;
    if (nextIndex >= sanitizedEvents.length) {
      return;
    }
    const nextEvent = sanitizedEvents[nextIndex];
    setSearchParams((prev) => {
      prev.set(ACTIVE_EVENT_IDS_2_QUERY_PARAM, nextEvent?.event.virtId ?? '');
      return prev;
    });
  }, [sanitizedEvents, sanitizedSelectedIndex, setSearchParams]);

  const onPrevClick = useCallback(() => {
    const prevIndex = sanitizedSelectedIndex - 1;
    if (prevIndex < 0) {
      return;
    }
    const prevEvent = sanitizedEvents[prevIndex];
    setSearchParams((prev) => {
      prev.set(ACTIVE_EVENT_IDS_2_QUERY_PARAM, prevEvent?.event.virtId ?? '');
      return prev;
    });
  }, [sanitizedEvents, sanitizedSelectedIndex, setSearchParams]);

  return {
    eventCards,
    selectedIndex: sanitizedSelectedIndex,
    onNextClick,
    onPrevClick,
  };
};
